<template>
    <!-- Gemeinsamer Formularteil für «Neuer Käufer» und «Käufer bearbeiten»
         (Entscheid Peter, 22.09.2026). Vorher trug jedes Fenster seine eigene
         Kopie aller Felder; die leere Länderauswahl vom 02.09. steckte genau
         deshalb nur in zwei von sieben Masken. Mehrere Wurzelknoten, damit die
         Felder Zellen im Raster der umgebenden `form-card` bleiben.

         Der Teil schreibt in das `useForm`-Objekt des Fensters. Das ist
         Absicht: es gibt genau einen Formularzustand, samt Fehlern und
         `processing`, und der gehört dem Fenster, das speichert. -->
    <div class="col-span-6">
        <label for="firstname" class="block text-sm font-medium text-gray-700">{{ $t("apartments.firstname") }} <RequiredMark /></label>
        <input type="text" name="firstname" id="firstname" v-model="form.firstname"
                class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" autofocus />
        <jet-input-error :message="form.errors.firstname" class="mt-2" />
    </div>

    <div class="col-span-6">
        <label for="lastname" class="block text-sm font-medium text-gray-700">{{ $t("apartments.lastname") }} <RequiredMark /></label>
        <input type="text" name="lastname" id="lastname" v-model="form.lastname"
                class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
        <jet-input-error :message="form.errors.lastname" class="mt-2" />
    </div>

    <div class="col-span-6">
        <label for="street" class="block text-sm font-medium text-gray-700">{{ $t("apartments.street") }}</label>
        <input type="text" name="street" id="street" v-model="form.street"
                class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
        <jet-input-error :message="form.errors.street" class="mt-2" />
    </div>

    <div class="col-span-2">
        <label for="zip" class="block text-sm font-medium text-gray-700">{{ $t("apartments.zip") }}</label>
        <input type="text" name="zip" id="zip" v-model="form.zip"
                class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
        <jet-input-error :message="form.errors.zip" class="mt-2" />
    </div>

    <div class="col-span-4">
        <label for="city" class="block text-sm font-medium text-gray-700">{{ $t("apartments.city") }}</label>
        <input type="text" name="city" id="city" v-model="form.city"
                class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
        <jet-input-error :message="form.errors.city" class="mt-2" />
    </div>


    <div class="col-span-6">
        <label for="city" class="block text-sm font-medium text-gray-700">{{ $t("apartments.country") }}</label>
        <Dropdown v-model="form.country"
                    :options="countries"
                    optionValue="code"
                    optionLabel="name"
                    :placeholder="$t('forms.selectCountry')"
                    class="mt-1 w-full" />
        <jet-input-error :message="form.errors.country" class="mt-2" />
    </div>

    <div class="col-span-6">
        <label for="phone" class="block text-sm font-medium text-gray-700">{{ $t("apartments.phone") }}</label>
        <input type="text" name="phone" id="phone" v-model="form.phone"
                :maxlength="$page.props.branding.mobile_number_max_digit || 12"
                @input="form.phone = form.phone.replace(/[^0-9]/g, '').slice(0, $page.props.branding.mobile_number_max_digit || 12)"
                class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
        <jet-input-error :message="form.errors.phone" class="mt-2" />
    </div>

    <div class="col-span-6">
        <label for="email" class="block text-sm font-medium text-gray-700">{{ $t("apartments.email") }} <RequiredMark v-if="emailRequired || form.invite" /></label>
        <input type="text" name="email" id="email" v-model="form.email"
                class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
        <jet-input-error :message="form.errors.email" class="mt-2" />
    </div>

    <!-- Einladen braucht eine E-Mail. Ohne sie ist der Schalter gesperrt,
         statt eine Einladung zu versprechen, die nicht hinausgehen kann. Nicht
         mehr angeboten wird er bei einem Käufer, der schon eingeladen ist; dort
         gibt es «Erneut einladen» im Wohnungskopf. -->
    <SwitchGroup v-if="!invited" as="div" class="flex items-start mt-4 mr-4 col-span-6" data-invite>
        <Switch v-model="form.invite"
                :disabled="!hasEmail"
                :class="[form.invite ? 'bg-minplan-lightgreybeige' : 'bg-gray-200', hasEmail ? '' : 'opacity-50 cursor-not-allowed', 'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-minplan-beige']">
            <span aria-hidden="true" :class="[form.invite ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200']" />
        </Switch>
        <SwitchLabel as="span" class="ml-3">
            <span class="block text-sm font-medium text-gray-900">{{ $t("apartments.inviteNow") }}</span>
            <span class="block text-xs text-gray-500">{{ hasEmail ? $t("apartments.inviteNowHint") : $t("apartments.inviteNeedsEmail") }}</span>
        </SwitchLabel>
    </SwitchGroup>

    <!-- «Benachrichtigen» wirkt erst ab der Einladung. Beim Erfassen erscheint
         der Schalter deshalb erst mit «Käufer jetzt einladen»; im Hintergrund
         bleibt er eingeschaltet und gilt ab der späteren Einladung. Beim
         Bearbeiten ist er immer da. -->
    <SwitchGroup v-if="mode === 'edit' || form.invite" as="div" class="flex items-center mt-2 mb-4 mr-4 hidden md:flex col-span-6" data-notify>
        <Switch v-model="form.notify"
                :class="[form.notify ? 'bg-minplan-lightgreybeige' : 'bg-gray-200', 'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-minplan-beige']">
            <span aria-hidden="true" :class="[form.notify ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200']" />
        </Switch>
        <SwitchLabel as="span" class="ml-3">
            <span class="text-sm font-medium text-gray-900">{{ $t("apartments.notify") }}</span>
        </SwitchLabel>
    </SwitchGroup>
</template>

<script>
import { defineComponent } from "vue";
import { countries } from "@/Shared/countries";
import JetInputError from "@/Jetstream/InputError";
import { Switch, SwitchGroup, SwitchLabel } from "@headlessui/vue";
import RequiredMark from "@/Shared/Components/RequiredMark";

export default defineComponent({
    name: "BuyerFormFields",
    props: {
        form: { type: Object, required: true },
        // "create" oder "edit": beim Erfassen erscheint «Benachrichtigen» erst
        // mit dem Einladen-Schalter.
        mode: { type: String, default: "create" },
        // Zu DIESER Wohnung ging schon eine Einladung hinaus (Spec §3b):
        // kein Einladen-Schalter mehr, dafür «Erneut einladen» im
        // Wohnungskopf.
        invited: { type: Boolean, default: false },
        // Die E-Mail ist schon ohne neues Einladen Pflicht, weil sie die
        // Anmeldung eines bestehenden Kontos ist — derselbe Begriff wie
        // `Buyer::needsEmail()` auf dem Server. Bewusst getrennt von
        // `invited`: ein Käufer mit Konto aus einer ANDEREN Wohnung ist hier
        // nicht eingeladen (Schalter bleibt), seine E-Mail ist aber Pflicht
        // (Stern). Vorher zeigte die Maske dort keinen Stern, und der Berater
        // bekam einen Fehler an einem Feld, das freiwillig aussah.
        emailRequired: { type: Boolean, default: false },
    },
    components: { JetInputError, Switch, SwitchGroup, SwitchLabel, RequiredMark },
    setup() {
        return { countries };
    },
    computed: {
        hasEmail() {
            return !!(this.form.email && String(this.form.email).trim());
        },
    },
    watch: {
        // Wird die E-Mail geleert, geht auch das Einladen aus.
        hasEmail(vorhanden) {
            if (!vorhanden) {
                this.form.invite = false;
            }
        },
    },
});
</script>
